import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'sonner';
import { RadialBarChart, RadialBar, ResponsiveContainer } from 'recharts';
import { GET_COMPLIANCE_REPORT } from '../graphql/queries';
import { RESCAN_IMAGE } from '../graphql/mutations';
import OsIcon from '../components/OsIcon';

function ScoreChart({ score }) {
  const percentage = Math.round(score || 0);
  let fill = '#d63031';
  if (percentage >= 90) fill = '#00b894';
  else if (percentage >= 70) fill = '#fdcb6e';
  else if (percentage >= 50) fill = '#e17055';

  const chartData = [{ name: 'Score', value: percentage, fill }];

  return (
    <div style={{ position: 'relative', width: 180, height: 180 }}>
      <ResponsiveContainer width="100%" height="100%">
        <RadialBarChart cx="50%" cy="50%" innerRadius="70%" outerRadius="100%" startAngle={90} endAngle={-270} data={chartData}>
          <RadialBar background clockWise dataKey="value" cornerRadius={10} />
        </RadialBarChart>
      </ResponsiveContainer>
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
        <span style={{ fontSize: 36, fontWeight: 700 }}>{percentage}</span>
        <br />
        <span style={{ fontSize: 14, color: '#636e72' }}>/ 100</span>
      </div>
    </div>
  );
}

function SeverityCard({ label, count, className }) {
  return (
    <div className={`severity-card ${className}`}>
      <div className="severity-card-count">{count || 0}</div>
      <div className="severity-card-label">{label}</div>
    </div>
  );
}

export default function ComplianceReport() {
  const { imageId } = useParams();
  const { data, loading, error } = useQuery(GET_COMPLIANCE_REPORT, {
    variables: { imageId },
  });
  const [rescanImage, { loading: rescanning }] = useMutation(RESCAN_IMAGE);

  if (loading) return <div className="loading">Loading compliance report...</div>;
  if (error) return <div className="error">Failed to load compliance report: {error.message}</div>;

  const report = data?.getComplianceReport;
  if (!report) return <div className="error">Compliance report not found for this image.</div>;

  const vulns = report.vulnerabilitySummary || {};

  const handleRescan = async () => {
    try {
      await rescanImage({ variables: { imageId } });
      toast.success('Re-scan initiated. Results will update shortly.');
    } catch (err) {
      toast.error(`Re-scan failed: ${err.message}`);
    }
  };

  const handleDownloadPdf = () => {
    toast.info('PDF download is not yet available. Coming soon.');
  };

  return (
    <div>
      <h2 className="page-title">Compliance Report</h2>

      <div className="card">
        <div className="detail-grid">
          <div className="detail-item"><label>Image</label><p>{report.imageName || imageId}</p></div>
          <div className="detail-item"><label>Blueprint</label><p>{report.blueprintName ? <OsIcon name={report.blueprintName} showLabel /> : '-'}</p></div>
          <div className="detail-item"><label>Status</label>
            <p>
              <span className={`badge badge-${report.status === 'COMPLIANT' ? 'success' : report.status === 'NON_COMPLIANT' ? 'error' : 'warning'}`}>
                {report.status}
              </span>
            </p>
          </div>
          <div className="detail-item"><label>Generated</label><p>{report.generatedAt ? new Date(report.generatedAt).toLocaleString() : '-'}</p></div>
        </div>
      </div>

      <div className="card" style={{ textAlign: 'center' }}>
        <h3>Overall Compliance Score</h3>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 40, marginTop: 16 }}>
          <ScoreChart score={report.overallScore} />
          <div style={{ textAlign: 'left' }}>
            <div className="detail-item">
              <label>CIS Benchmark Level</label>
              <p style={{ fontSize: 24, fontWeight: 700 }}>{report.cisLevel || '-'}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <h3>Vulnerability Summary</h3>
        <div className="severity-cards">
          <SeverityCard label="Critical" count={vulns.CRITICAL} className="severity-critical" />
          <SeverityCard label="High" count={vulns.HIGH} className="severity-high" />
          <SeverityCard label="Medium" count={vulns.MEDIUM} className="severity-medium" />
          <SeverityCard label="Low" count={vulns.LOW} className="severity-low" />
          <SeverityCard label="Info" count={vulns.INFORMATIONAL} className="severity-info" />
        </div>
      </div>

      {report.recommendations?.length > 0 && (
        <div className="card">
          <h3>Recommendations</h3>
          <ul className="recommendations-list">
            {report.recommendations.map((rec, i) => (
              <li key={i}>{rec}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="actions">
        <button className="btn btn-primary" onClick={handleRescan} disabled={rescanning}>
          {rescanning ? 'Initiating Re-scan...' : 'Re-scan Image'}
        </button>
        <button className="btn btn-primary" style={{ background: '#6c5ce7' }} onClick={handleDownloadPdf}>
          Download PDF
        </button>
        <Link to={`/images/${imageId}`} className="btn" style={{ background: '#dfe6e9' }}>
          Back to Image
        </Link>
      </div>
    </div>
  );
}
